import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataQueryApi, type DataQueryListQuery } from '@/features/data-query/api';
import { DataQueryDetail } from '@/features/data-query/detail';
import { DataQueryFormat as Format, OUTCOME_LABELS } from '@/features/data-query/format';
import type { DataQueryExecution, DataQueryResponse } from '@/features/data-query/types';
import { DetailDrawer } from '@/features/project-board/components/DetailDrawer';
import { Pagination } from '@/shared/ui/Pagination';

const INPUT = 'rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px]';
const PAGE_SIZES = [20, 50, 100];

export function DataQueryView(): React.JSX.Element {
  const [filters, setFilters] = useState(() => Format.initial());
  const [query, setQuery] = useState<DataQueryListQuery>(() => Format.query(filters));
  const [data, setData] = useState<DataQueryResponse | null>(null);
  const [selected, setSelected] = useState<DataQueryExecution | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError('');
    DataQueryApi.fetch(query).then((result) => {
      if (!active) return;
      setData(result);
      setSelected((current) => result.executions.find((execution) => execution.executionId === current?.executionId) ?? null);
    }).catch((reason: Error) => {
      if (active) { setError(reason.message); setData(null); setSelected(null); }
    }).finally(() => { if (active) setLoading(false); });
    // 늦게 도착한 이전 페이지가 새 조건의 결과를 덮지 않게 한다.
    return () => { active = false; };
  }, [query]);
  useEffect(reload, [reload]);

  const apply = () => {
    try { setQuery(Format.query(filters, 1, query.size)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : '조회 조건을 확인해 주세요.'); }
  };
  const detail = useMemo(() => selected ? DataQueryDetail.payload(selected) : null, [selected]);

  return <div className="space-y-4">
    <header className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h1 className="text-xl font-bold">데이터 추출 이력</h1>
        <p className="text-[12px] text-muted">누가 어떤 요청으로 어떤 SQL을 실행해 어떤 결과를 받았는지 확인합니다.</p>
      </div>
      <span className="text-[12px] text-muted">{data ? `총 ${Format.number(data.total)}건` : ''}</span>
    </header>
    <section className="flex flex-wrap items-end gap-2 rounded-lg border border-line bg-surface p-3 text-[12px]">
      <Filter label="시작일">
        <input
          type="date"
          value={filters.from}
          onChange={(event) => setFilters({ ...filters, from: event.target.value })}
          className={INPUT}
        />
      </Filter>
      <Filter label="종료일">
        <input
          type="date"
          value={filters.to}
          onChange={(event) => setFilters({ ...filters, to: event.target.value })}
          className={INPUT}
        />
      </Filter>
      <Filter label="요청자">
        <input
          value={filters.requester}
          onChange={(event) => setFilters({ ...filters, requester: event.target.value })}
          placeholder="이름 또는 U…"
          className={INPUT}
        />
      </Filter>
      <Filter label="결과">
        <select
          value={filters.outcome}
          onChange={(event) => setFilters({ ...filters, outcome: event.target.value })}
          className={INPUT}
        >
          <option value="">전체</option>{Object.entries(OUTCOME_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select>
      </Filter>
      <button
        type="button"
        onClick={apply}
        disabled={loading}
        className="rounded-md bg-accent px-4 py-1.5 font-medium text-white disabled:opacity-50"
      >{loading ? '조회 중…' : '조회'}</button>
    </section>
    {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-[12px] text-rose-700">{error}</div>}
    <div className="overflow-x-auto rounded-lg border border-line bg-surface" aria-busy={loading}>
      <table className="w-full text-left text-[12px]">
        <thead className="border-b border-line bg-line-soft text-muted">
          <tr>
            <th className="p-3">시작 시각 (KST)</th>
            <th>요청자</th>
            <th>요청 문장</th>
            <th>결과</th>
            <th>행 수</th>
            <th className="pr-3">소요 시간</th>
          </tr>
        </thead>
        <tbody>{(data?.executions ?? []).map((execution) => <tr
          key={execution.executionId}
          onClick={() => setSelected(execution)}
          className="cursor-pointer border-b border-line hover:bg-line-soft last:border-0"
        >
          <td className="whitespace-nowrap p-3">{Format.kst(execution.startedAt)}</td>
          <td>{Format.requester(execution)}</td>
          <td className="max-w-sm p-2">
            <button
              type="button"
              onClick={() => setSelected(execution)}
              className="text-left text-accent hover:underline"
            >{Format.preview(execution.question)}</button>
          </td>
          <td>{Format.label(execution.outcome)}</td>
          <td>{Format.number(execution.rowCount)}</td>
          <td className="whitespace-nowrap pr-3">{Format.number(execution.durationMs)}ms</td>
        </tr>)}</tbody>
      </table>
      {!error && !loading && (data?.executions.length ?? 0) === 0 && <div className="p-8 text-center text-muted">조건에 맞는 추출 이력이 없습니다.</div>}
    </div>
    <div className="flex items-center justify-between gap-2">
      <label className="flex items-center gap-2 text-[12px] text-muted">페이지당<select
        value={query.size}
        onChange={(event) => setQuery({ ...query, page: 1, size: Number(event.target.value) })}
        className={INPUT}
      >{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}건</option>)}</select>
      </label>
      <Pagination
        page={data?.page ?? 1}
        totalPages={Format.pages(data)}
        onChange={(page) => setQuery({ ...query, page })}
      />
    </div>
    <DetailDrawer payload={detail} onClose={() => setSelected(null)} />
  </div>;
}

function Filter({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <label className="flex flex-col gap-1 text-muted">
    <span>{label}</span>{children}</label>;
}
